import { useEffect, useState } from 'react';
import { api } from '../api';
import { formatEuros } from '../format';
import type { ProfitabilityResponse } from '../types';

function percent(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(2).replace('.', ',')} %`;
}

function Indicator({ label, value, note }: { label: string; value: string; note?: string | undefined }) {
  return (
    <div className="indicator">
      <dt>{label}</dt>
      <dd>{value}</dd>
      {note && <p>{note}</p>}
    </div>
  );
}

export function ProfitabilityPanel({ year }: { year: string }) {
  const [data, setData] = useState<ProfitabilityResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        setData(await api<ProfitabilityResponse>(`/api/admin/finances/profitability?year=${year}`));
        setError('');
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
      }
    };
    void load();
  }, [year]);

  if (error) return <div className="inline-message error" role="alert">{error}</div>;
  if (!data) return <p className="muted">Chargement…</p>;

  const p = data.profitability;

  return (
    <section className="profitability">
      <h3 className="subhead">Rentabilité {p.year}</h3>

      {p.missing.length > 0 && (
        <ul className="alert-list">
          <li className="info">
            Données manquantes en configuration : {p.missing.join(', ')}. Les indicateurs qui en dépendent
            restent vides plutôt que d'afficher une estimation.
          </li>
        </ul>
      )}

      <dl className="indicator-grid">
        <Indicator label="Rendement brut" value={percent(p.grossYield)} note={data.notes.grossYield} />
        <Indicator label="Rendement brut réel" value={percent(p.actualGrossYield)} note={data.notes.actualGrossYield} />
        <Indicator label="Rendement net de charges" value={percent(p.netYield)} note={data.notes.netYield} />
        <Indicator
          label="Cash-flow mensuel"
          value={formatEuros(p.cashFlowMonthlyCents)}
          note={data.notes.cashFlow}
        />
      </dl>

      <dl className="indicator-grid">
        <Indicator
          label="Taux d'occupation"
          value={percent(p.occupancyRate)}
          note={data.notes.occupancyRate}
        />
        <Indicator
          label="Taux de recouvrement"
          value={percent(p.collectionRate)}
          note={data.notes.collectionRate}
        />
      </dl>

      <dl className="detail-grid">
        <div><dt>Coût total d'acquisition</dt><dd>{formatEuros(p.investmentCents)}</dd></div>
        <div><dt>Loyer théorique plein (12 mois)</dt><dd>{formatEuros(p.fullYearRentCents)}</dd></div>
        <div>
          <dt>Loyers appelés sur {p.elapsedMonths} mois</dt>
          <dd>{formatEuros(p.rentDueCents)} sur {formatEuros(p.expectedToDateCents)} possibles</dd>
        </div>
        <div><dt>Loyers encaissés</dt><dd>{formatEuros(p.rentReceivedCents)}</dd></div>
        <div><dt>Dépenses de l'année</dt><dd>{formatEuros(p.expensesCents)}</dd></div>
        <div><dt>Charges annuelles fixes</dt><dd>{formatEuros(p.fixedChargesCents)}</dd></div>
        <div>
          <dt>Échéances de crédit sur {p.elapsedMonths} mois</dt>
          <dd>{formatEuros(p.creditToDateCents)} sur {formatEuros(p.creditAnnualCents)} annuels</dd>
        </div>
        <div>
          <dt>Cash-flow constaté</dt>
          <dd>{formatEuros(p.cashFlowToDateCents)}</dd>
        </div>
        <div>
          <dt>Cash-flow projeté sur 12 mois</dt>
          <dd>{formatEuros(p.cashFlowAnnualCents)}</dd>
        </div>
      </dl>

      <div className="deposit-note">
        <strong>Ce que ce tableau ne dit pas</strong>
        <span>{data.disclaimer}</span>
      </div>
    </section>
  );
}
